import { Box, Typography } from "@mui/material";
import type { ParsedDtcAnswer, DtcContentSection } from "../../utils/dtcAnswerUtils";
import {
  ANSWER_BODY_SX,
  ANSWER_HEADING_SX,
  ANSWER_TITLE_SX,
  PROCEDURE_SECTION_BADGE_PILL_SX,
} from "../../constants/ragUiConstants";
import { RagMarkdownContent } from "./RagMarkdownContent";

function DtcSectionBody({ section }: { section: DtcContentSection }) {
  if (section.kind === "list" && section.items?.length) {
    return (
      <Box
        component="ul"
        sx={{
          pl: 2,
          my: 0,
          listStyleType: "disc",
          "& li": { display: "list-item", mb: 0.25 },
        }}
      >
        {section.items.map((item, i) => (
          <Box
            component="li"
            key={i}
            data-testid="dtc-section-body"
            sx={ANSWER_BODY_SX}
          >
            <RagMarkdownContent text={item} dense />
          </Box>
        ))}
      </Box>
    );
  }

  if (!section.text?.trim()) return null;

  return (
    <Box data-testid="dtc-section-body" sx={ANSWER_BODY_SX}>
      <RagMarkdownContent text={section.text} dense />
    </Box>
  );
}

export function DtcResponseView({ data }: { data: ParsedDtcAnswer }) {
  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.75,
          mb: data.title ? 0.75 : 0,
        }}
      >
        <Box component="span" sx={PROCEDURE_SECTION_BADGE_PILL_SX}>
          {data.code}
        </Box>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: "primary.50",
            color: "primary.main",
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {data.category}
        </Box>
      </Box>

      {data.title ? (
        <Typography
          component="h2"
          data-testid="answer-title"
          sx={{
            ...ANSWER_TITLE_SX,
            mb: data.sections.length ? 1 : 0,
          }}
        >
          {data.title}
        </Typography>
      ) : null}

      {data.sections.map((section, i) => (
        <Box key={`${section.label}-${i}`} sx={{ mt: i === 0 ? 0 : 1.25 }}>
          <Typography
            component="h3"
            data-testid="answer-heading"
            sx={{
              ...ANSWER_HEADING_SX,
              mb: 0.5,
            }}
          >
            {section.label}
          </Typography>
          <DtcSectionBody section={section} />
        </Box>
      ))}
    </Box>
  );
}
